import { useId } from 'react';
import { MARK, WORDMARK } from './LogoPaths';
import { cn } from '@/components/ui/cn';

export type LogoVariant = 'ink' | 'brand' | 'inverse' | 'mono';

const variantClass: Record<LogoVariant, string> = {
  ink: 'text-ink',
  brand: 'text-primary',
  inverse: 'text-brand-cream',
  mono: '',
};

export interface AtteLogoProps {
  /** Alto en px (24 en la interfaz; mínimo absoluto 16). */
  height?: number;
  variant?: LogoVariant;
  title?: string;
  className?: string;
}

/**
 * Logotipo "Atte" (Corinthia convertida a trazos, D-26). Relleno y trazo del mismo color con el
 * `strokeWidth` del generador para que los finos no desaparezcan. Nunca se escribe con la fuente.
 */
export function AtteLogo({ height = 24, variant = 'ink', title = 'Atte', className }: AtteLogoProps) {
  const titleId = useId();
  const width = (height * WORDMARK.width) / WORDMARK.height;
  return (
    <svg
      viewBox={WORDMARK.viewBox}
      width={width}
      height={height}
      role="img"
      aria-labelledby={titleId}
      className={cn('inline-block shrink-0', variantClass[variant], className)}
    >
      <title id={titleId}>{title}</title>
      <path
        d={WORDMARK.d}
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={WORDMARK.strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface AtteMonogramProps {
  /** Lado en px (16–512). */
  size?: number;
  title?: string;
  className?: string;
  /** Decorativo cuando el nombre del producto ya está escrito al lado. */
  decorative?: boolean;
}

/**
 * Isotipo: la "A" en Crema dentro del cuadrado redondeado Eucalipto (radio 116/512).
 * Hasta 32 px usa la variante de finos reforzados (`MARK.dSmall`).
 */
export function AtteMonogram({ size = 24, title = 'Atte', className, decorative }: AtteMonogramProps) {
  const titleId = useId();
  const small = size <= 32;
  return (
    <svg
      viewBox={MARK.viewBox}
      width={size}
      height={size}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-labelledby={decorative ? undefined : titleId}
      className={cn('inline-block shrink-0', className)}
    >
      {decorative ? null : <title id={titleId}>{title}</title>}
      <rect width={MARK.size} height={MARK.size} rx={MARK.radius} className="fill-brand-eucalyptus" />
      <path
        d={small ? MARK.dSmall : MARK.d}
        className="fill-brand-cream stroke-brand-cream"
        strokeWidth={small ? MARK.strokeWidthSmall : MARK.strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Alias cortos. */
export { AtteLogo as Logo, AtteMonogram as Mark };
